import { useCallback, useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { useBootstrapWS } from "./useBootstrapWS";
import { toast } from "sonner";

/** Load and mutate runtime status for a single production department. */
export function useDepartmentRuntime(department) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    if (!department) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/production-monitoring/${department}/status`);
      setData(res.data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [department]);

  useEffect(() => {
    reload();
  }, [reload]);

  useBootstrapWS(["production_monitoring"], reload);

  const startSection = async (sectionSlug) => {
    setSubmitting(true);
    try {
      await api.post(`/production-monitoring/${department}/start`, { section_slug: sectionSlug });
      toast.success("Machine started");
      await reload();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const stopSection = async (sectionSlug, { stopType = "normal", breakdownReason = "", remarks = "" } = {}) => {
    setSubmitting(true);
    try {
      await api.post(`/production-monitoring/${department}/stop`, {
        section_slug: sectionSlug,
        stop_type: stopType,
        breakdown_reason: breakdownReason.trim() || null,
        remarks: remarks.trim() || null,
      });
      toast.success(stopType === "breakdown" ? "Breakdown recorded" : "Machine stopped");
      await reload();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    data,
    loading,
    submitting,
    reload,
    startSection,
    stopSection,
    sections: data?.sections || [],
    recentLogs: data?.recent_logs || [],
    summary: data?.summary || {},
  };
}
